import { type FC } from 'preact/compat';

import clsx from 'clsx';

import { useWrapperAdsState } from '@/entities/ad-banner/model/wrapper-ads.store';
import { AdType } from '@/entities/ad-banner/model/wrapper-ads.types';
import { AdBanner } from '@/entities/ad-banner/ui/ad-banner';
import { AddToHomeScreenButton } from '@/entities/buttons/add-to-home-screen-button';
import { FavoriteToggle } from '@/entities/buttons/favorite-button';
import { PlayButton } from '@/entities/buttons/play-button';
import { ShareButton } from '@/entities/buttons/share-button';

import { type Game, GameOrientation } from '@/shared/api/game/types';
import { AD_NETWORK } from '@/shared/api/user/types';
import {
  useAppState,
  useAppStateRaw,
  useUserProfile,
} from '@/shared/stores/app-store/app-store';

import YandexAdBanner from '@/widget/yandex-banner/YandexBanner';

import s from './index.module.scss';

interface Props {
  game: Game;
  pdOpen: boolean;
  titleOnly?: boolean;
}

export const Panel: FC<Props> = ({ game, pdOpen, titleOnly = false }) => {
  const { screenOrientation } = useAppState();
  const { ads } = useWrapperAdsState();
  const isWeb = useAppStateRaw.getState().isWebMode;
  const user = useUserProfile();

  const isPortrait = screenOrientation === GameOrientation.PORTRAIT;
  const isYNetworkAvailable = user?.avdNetworks.includes(AD_NETWORK.Y);

  const preloadAd = ads?.find(
    (ad) => ad.type === AdType.BANNER || ad.type === AdType.BLOCK
  );

  const renderAds = () => {
    if (isYNetworkAvailable && isPortrait && !game?.fight) {
      return <YandexAdBanner />;
    }

    if (preloadAd && !isYNetworkAvailable && !game?.fight) {
      return <AdBanner className={s['preload-ad']} ad={preloadAd} />;
    }

    return null;
  };

  const renderHeader = () => {
    const headerClass = !game?.fight || isPortrait ? s.header : s['landscape-header'];

    return (
      <div className={clsx(headerClass)}>
        {/* {game?.fight &&(
          <PvpPanel className={s['pvp-panel']} fight={game.fight} gameKey={game.key} />
        )} */}

        <div className={s.title}>
          <img src={game?.iconUrl} className={s['game-icon']} alt="game img" />
          <h1 className={s.name}>{game?.title}</h1>
        </div>

        {!titleOnly && (
          <div className={s['game-card-actions']}>
            {!isWeb && <ShareButton place="start" />}
            {(!game?.fight || isPortrait) && <AddToHomeScreenButton />}
            <FavoriteToggle gameKey={game.key} defaultValue={game.favorite} />
          </div>
        )}
      </div>
    );
  };

  if (titleOnly) {
    return (
      <div className={s['title-only-panel']}>
        <div className={s.container}>{renderHeader()}</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={clsx(s.panel, { [s.appearance]: pdOpen, [s.disappearance]: !pdOpen })}
      >
        <div className={s.container}>
          {renderAds()}
          {renderHeader()}
        </div>
        <PlayButton />
      </div>
    </>
  );
};
